<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4 mb-5" style="max-width:700px">
        <h3>Slett dagsoppgjør-bilag</h3>

        <div v-show="!loading">
            <div v-if="locations.length === 0" class="mt-4">
              <span class="message-info">Ingen avdelinger har Tripletex aktivert. Aktiver det under Avdelinger.</span>
            </div>

            <div v-else-if="!configured" class="mt-4">
              <span class="message-info">Tripletex-konto er ikke konfigurert for denne avdelingen. Gå til <a href="/tripletex">Tripletex innstillinger</a> for å sette opp.</span>
            </div>

            <div v-if="configured">
                <div v-if="hasMultiLocations()" class="mt-4">
                  <span class="ingredient-title">Velg avdeling</span>
                  <select class="form-select" v-model="locationId" @change="onLocationChange()">
                    <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
                  </select>
                </div>

                <div class="mt-4">
                  <p class="text-muted">Søk etter dagsoppgjør-bilag i Tripletex for en periode. Du kan se bilagene før du velger å slette.<br>
                    <strong>Merk:</strong> Tripletex tillater kun sletting av det nyeste bilaget først. Slett derfor i rekkefølge fra nyest til eldst.</p>
                  <SelectDate ref="selectDateFrom" label="Velg fra og med dato"/>
                  <SelectDate ref="selectDateTo" label="Velg til og med dato"/>
                  <button type="button" class="bo_btn mt-2" @click="searchVouchers"> Søk bilag </button>

                  <!-- Search results -->
                  <div v-if="searchCompleted" class="mt-3">
                    <div v-if="vouchers.length === 0" class="message-info">
                      <p>{{ searchMessage }}</p>
                    </div>

                    <div v-else>
                      <p class="mb-2"><strong>{{ vouchers.length }} dagsoppgjør-bilag funnet:</strong></p>
                      <table class="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Bilagsnr.</th>
                            <th>Dato</th>
                            <th>Beskrivelse</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(v, index) in vouchers" :key="index">
                            <td>{{ v.number }}</td>
                            <td>{{ v.date }}</td>
                            <td>{{ v.description }}</td>
                            <td><button type="button" class="btn btn-sm btn-outline-danger" @click="deleteSingleVoucher(v, index)">Slett</button></td>
                          </tr>
                        </tbody>
                      </table>

                      <button type="button" class="bo_btn mt-2 btn-danger" @click="deleteVouchers">
                        Slett {{ vouchers.length }} bilag
                      </button>
                    </div>
                  </div>

                  <!-- Delete results -->
                  <div v-if="deleteCompleted" class="mt-3">
                    <div :class="deleteResult.success ? 'message-success' : 'message-error'">
                      <p>{{ deleteResult.message }}</p>
                      <p v-if="deleteResult.deletedCount !== undefined">Slettet: {{ deleteResult.deletedCount }} av {{ deleteResult.totalFound }} | Feil: {{ deleteResult.errorCount }}</p>
                    </div>
                    <div v-if="deleteResult.errors" class="mt-2 message-error">
                      <span class="ingredient-title">Feildetaljer:</span>
                      <ul>
                        <li v-for="(error, index) in deleteResult.errors" :key="index">{{ error }}</li>
                      </ul>
                    </div>
                  </div>
                </div>
            </div>
        </div>
      </div>
    <ConfirmDialogue ref="confirmDialogue"/>
  </div>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue";
  import SettingsService from "@/services/settings/SettingsService";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";

  export default {
    components: {LoadingSpinner, SelectDate, ConfirmDialogue},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        configured: false,
        searchCompleted: false,
        searchMessage: '',
        vouchers: [],
        deleteCompleted: false,
        deleteResult: {}
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'TRIPLETEX');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.checkSyncStatus();
            } else {
              this.configured = false;
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      onLocationChange(){
        this.searchCompleted = false;
        this.deleteCompleted = false;
        this.vouchers = [];
        this.checkSyncStatus();
      },
      checkSyncStatus(){
        SettingsService.getTripletexSyncStatus(this.locationId).then(
            (response) => {
              if(response.data !== null){
                this.configured = response.data.configured;
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      searchVouchers(){
        this.loading = true;
        this.searchCompleted = false;
        this.deleteCompleted = false;
        this.vouchers = [];
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.searchZReportVouchers(this.locationId, fromDate, toDate).then(
            (response) => {
              this.searchCompleted = true;
              this.vouchers = response.data.vouchers || [];
              this.searchMessage = response.data.message || '';
              this.loading = false;
            },
            (error) => {
              this.searchCompleted = true;
              this.searchMessage = 'Feil ved søk etter bilag';
              console.log(error);
              this.loading = false;
            }
        );
      },
      async deleteSingleVoucher(voucher, index){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Slett bilag',
          message: 'Er du sikker på at du vil slette bilag ' + voucher.number + ' (' + voucher.description + ')?',
          okButton: 'Slett',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;
        this.deleteCompleted = false;

        SettingsService.deleteSingleVoucher(this.locationId, voucher.id).then(
            (response) => {
              if (response.data.success) {
                this.vouchers.splice(index, 1);
              } else {
                this.deleteCompleted = true;
                this.deleteResult = response.data;
              }
              this.loading = false;
            },
            (error) => {
              this.deleteCompleted = true;
              this.deleteResult = { success: false, message: 'Feil ved sletting av bilag' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      async deleteVouchers(){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Slett bilag',
          message: 'Er du sikker på at du vil slette ' + this.vouchers.length + ' dagsoppgjør-bilag fra Tripletex?',
          okButton: 'Slett',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;
        this.deleteCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.deleteZReportVouchers(this.locationId, fromDate, toDate).then(
            (response) => {
              this.deleteCompleted = true;
              this.deleteResult = response.data;
              this.searchCompleted = false;
              this.vouchers = [];
              this.loading = false;
            },
            (error) => {
              this.deleteCompleted = true;
              this.deleteResult = { success: false, message: 'Feil ved sletting av bilag' };
              console.log(error);
              this.loading = false;
            }
        );
      }
    }
  };
</script>
